import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./StarRating.css";

const StarRating = ({ postId, averageRating, userRating, ratingsCount, onRate, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const [hoverRating, setHoverRating] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const starContainerRef = useRef(null);

  const dir = i18n.dir();

  // Calculate which star/half-star is being hovered
  const handleMouseMove = useCallback((e) => {
    if (disabled || isSubmitting) return;
    
    const container = starContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const containerWidth = rect.width;
    const starWidth = containerWidth / 5; // 5 stars total
    
    // Calculate which star (0-4)
    let starIndex = Math.floor(x / starWidth);
    starIndex = Math.max(0, Math.min(4, starIndex)); // Clamp between 0 and 4
    
    // Calculate if it's the left half (half-star) or right half (full star)
    const positionInStar = (x % starWidth) / starWidth;
    const isHalfStar = positionInStar < 0.5;
    
    // Calculate rating value
    const rating = starIndex + 1 + (isHalfStar ? -0.5 : 0);
    setHoverRating(rating);
  }, [disabled, isSubmitting]);

  const handleMouseLeave = useCallback(() => {
    setHoverRating(null);
  }, []);

  const handleClick = useCallback(async (e) => {
    if (disabled || isSubmitting) return;
    
    const container = starContainerRef.current;
    if (!container || !onRate) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const containerWidth = rect.width;
    const starWidth = containerWidth / 5;
    
    // Calculate which star (0-4)
    let starIndex = Math.floor(x / starWidth);
    starIndex = Math.max(0, Math.min(4, starIndex));
    
    // Calculate if it's the left half (half-star) or right half (full star)
    const positionInStar = (x % starWidth) / starWidth;
    const isHalfStar = positionInStar < 0.5;
    
    // Calculate rating value (0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0)
    const rating = starIndex + 1 + (isHalfStar ? -0.5 : 0);
    
    setIsSubmitting(true);
    try {
      await onRate(postId, rating);
    } catch (error) {
      console.error("Error rating post:", error);
    } finally {
      setIsSubmitting(false);
      setHoverRating(null);
    }
  }, [disabled, isSubmitting, onRate, postId]);

  // Determine star fill state
  const getStarState = (index) => {
    const displayRating = hoverRating !== null ? hoverRating : (averageRating || 0);
    const starValue = index + 1;
    const halfStarValue = starValue - 0.5;
    
    if (displayRating >= starValue) {
      return 'full';
    } else if (displayRating >= halfStarValue) {
      return 'half';
    } else {
      return 'empty';
    }
  };

  // Render a single star
  const renderStar = (index) => {
    const state = getStarState(index);
    const starValue = index + 1;
    const uniqueId = `star-${postId}-${index}`;
    
    return (
      <span
        key={index}
        className={`star-rating-star star-rating-star-${state}`}
        data-star-value={starValue}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id={`half-star-clip-${uniqueId}`}>
              <rect x="0" y="0" width="12" height="24" />
            </clipPath>
          </defs>
          {/* Base star outline - always shown */}
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill={state === 'full' ? '#FBBF24' : 'none'}
            stroke={state === 'full' ? '#FBBF24' : state === 'half' ? '#FBBF24' : '#D1D5DB'}
            strokeWidth="1.5"
          />
          {/* Half star fill (left half) - only for half state */}
          {state === 'half' && (
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="#FBBF24"
              clipPath={`url(#half-star-clip-${uniqueId})`}
            />
          )}
        </svg>
      </span>
    );
  };

  const displayRating = averageRating !== null && averageRating !== undefined 
    ? averageRating.toFixed(1) 
    : null;

  // Stars should always render, even without backend data (will show as empty)
  return (
    <div 
      className="star-rating-container" 
      dir={dir}
      style={{ 
        opacity: disabled || isSubmitting ? 0.6 : 1,
        pointerEvents: disabled || isSubmitting ? 'none' : 'auto',
        display: 'flex', // Ensure it's visible
        visibility: 'visible' // Ensure it's visible
      }}
    >
      <div
        ref={starContainerRef}
        className="star-rating-stars"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: disabled || isSubmitting ? 'not-allowed' : 'pointer' }}
      >
        {[0, 1, 2, 3, 4].map(renderStar)}
      </div>
      {displayRating && (
        <span className="star-rating-text">
          {displayRating}
        </span>
      )}
      {!displayRating && (
        <span className="star-rating-text star-rating-text-empty">
          {t("postFeed.rating.noRatings") || "No ratings yet"}
        </span>
      )}
    </div>
  );
};

export default StarRating;
